import type { FableCharacter, VoiceRecord } from '../api/network_calls';

export const isActiveGenerationRequestId = (requestId: unknown): boolean =>
  typeof requestId === 'string'
  && requestId.trim().length > 0
  && requestId !== 'training_complete'
  && requestId !== 'complete';

const isGeneratingStatus = (status: unknown) =>
  status === 'pending' || status === 'generating';

const isCompleteStatus = (status: unknown) =>
  status === 'complete' || status === 'completed';

const isFailedStatus = (status: unknown) => status === 'failed';

export const isCharacterFailedRecord = (character?: FableCharacter | null): boolean => Boolean(
  character
  && (
    isFailedStatus(character.status)
    || isFailedStatus(character.generationStatus)
  )
);

export const isVoiceFailedRecord = (voice?: VoiceRecord | null): boolean => Boolean(
  voice
  && (
    isFailedStatus(voice.status)
    || isFailedStatus(voice.generationStatus)
  )
);

export const isCharacterGeneratingRecord = (character?: FableCharacter | null): boolean => {
  if (!character?.characterId && !character?.id) return false;

  if (
    character.pending === false
    || character.finishedTraining === true
    || isCompleteStatus(character.status)
    || isCompleteStatus(character.generationStatus)
    || isFailedStatus(character.status)
    || isFailedStatus(character.generationStatus)
  ) {
    return false;
  }

  return Boolean(
    character.pending === true
    || isGeneratingStatus(character.status)
    || isGeneratingStatus(character.generationStatus)
    || (
      character.finishedTraining === false
      && isActiveGenerationRequestId(character.requestId)
    ),
  );
};

export const getVoicePreviewUrl = (voice?: VoiceRecord | null): string | null =>
  voice?.signedPreviewUrl || voice?.previewUrl || voice?.referenceUrl || null;

export const isVoiceGeneratingRecord = (voice?: VoiceRecord | null): boolean => {
  if (!voice?.voiceId) return false;

  const status = voice.status || voice.generationStatus;
  if (
    voice.pending === false
    || isCompleteStatus(status)
    || isCompleteStatus(voice.generationStatus)
    || isFailedStatus(status)
    || isFailedStatus(voice.generationStatus)
  ) {
    return false;
  }

  return Boolean(
    voice.pending === true
    || isGeneratingStatus(status)
    || isGeneratingStatus(voice.generationStatus)
    || (isActiveGenerationRequestId(voice.requestId) && !getVoicePreviewUrl(voice))
    || !getVoicePreviewUrl(voice),
  );
};

export const normalizeGenerationProgress = (progress: unknown): number => {
  const parsed = Number(progress);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.min(100, Math.round(parsed)));
};

export const mergeCharactersPreservingPending = (
  current: FableCharacter[] = [],
  incoming: FableCharacter[] = [],
  options: {
    authoritative?: boolean;
    activeGenerationIds?: string[];
  } = {},
): FableCharacter[] => {
  if (options.authoritative) {
    return incoming;
  }

  const incomingIds = new Set(
    incoming
      .map((character) => character.characterId || character.id)
      .filter(Boolean),
  );
  const activeGenerationIds = Array.isArray(options.activeGenerationIds)
    ? new Set(options.activeGenerationIds)
    : undefined;
  const localPending = current.filter((character) => {
    const characterId = character.characterId || character.id;
    return Boolean(
      characterId
      && isCharacterGeneratingRecord(character)
      && (!activeGenerationIds || activeGenerationIds.has(characterId))
      && !incomingIds.has(characterId),
    );
  });

  return [...localPending, ...incoming];
};

export const mergeVoicesPreservingPending = (
  current: VoiceRecord[] = [],
  incoming: VoiceRecord[] = [],
  options: {
    authoritative?: boolean;
    activeGenerationIds?: string[];
  } = {},
): VoiceRecord[] => {
  if (options.authoritative) {
    return incoming;
  }

  const incomingIds = new Set(incoming.map((voice) => voice.voiceId).filter(Boolean));
  const activeGenerationIds = Array.isArray(options.activeGenerationIds)
    ? new Set(options.activeGenerationIds)
    : undefined;
  const localPending = current.filter((voice) => (
    voice.voiceId
    && isVoiceGeneratingRecord(voice)
    && (!activeGenerationIds || activeGenerationIds.has(voice.voiceId))
    && !incomingIds.has(voice.voiceId)
  ));

  return [...localPending, ...incoming];
};

export const normalizePendingCharacter = (
  character: FableCharacter,
): FableCharacter => ({
  ...character,
  pending: true,
  finishedTraining: false,
  status: isGeneratingStatus(character.status) ? character.status : 'generating',
  generationStatus: isGeneratingStatus(character.generationStatus)
    ? character.generationStatus
    : 'generating',
  progress: normalizeGenerationProgress(character.progress),
});

export const mergeCompletedCharacter = (
  characters: FableCharacter[] = [],
  payload: {
    characterId?: string;
    requestId?: string;
    character?: FableCharacter;
  },
): FableCharacter[] => {
  const characterId = payload.character?.characterId || payload.characterId;
  if (!characterId) return characters;

  const existingIndex = characters.findIndex(
    (character) => (character.characterId || character.id) === characterId,
  );
  const existingCharacter = existingIndex >= 0 ? characters[existingIndex] : undefined;
  const completedCharacter: FableCharacter = {
    ...(existingCharacter || {}),
    ...(payload.character || {}),
    characterId,
    requestId: 'complete',
    pending: false,
    finishedTraining: true,
    status: 'complete',
    generationStatus: 'complete',
    progress: 100,
  };

  if (existingIndex < 0) return [...characters, completedCharacter];
  return characters.map((character, index) => (
    index === existingIndex ? completedCharacter : character
  ));
};

export const normalizeVoiceForState = (voice: VoiceRecord): VoiceRecord => {
  if (!isVoiceGeneratingRecord(voice)) {
    return voice;
  }

  return {
    ...voice,
    pending: true,
    status: isGeneratingStatus(voice.status) ? voice.status : 'generating',
    generationStatus: isGeneratingStatus(voice.generationStatus)
      ? voice.generationStatus
      : 'generating',
    progress: normalizeGenerationProgress(voice.progress),
  };
};

export const mergeCompletedVoice = (
  voices: VoiceRecord[] = [],
  voiceId: string,
  voice?: VoiceRecord,
): VoiceRecord[] => {
  const existingIndex = voices.findIndex((item) => item.voiceId === voiceId);
  const existingVoice = existingIndex >= 0 ? voices[existingIndex] : undefined;
  const completedVoice: VoiceRecord = {
    ...(existingVoice || {}),
    ...(voice || {}),
    userId: voice?.userId || existingVoice?.userId || '',
    voiceId,
    voiceName: voice?.voiceName || existingVoice?.voiceName || 'Voice',
    voiceDescription: voice?.voiceDescription || existingVoice?.voiceDescription || '',
    gender: voice?.gender || existingVoice?.gender || 'neutral',
    previewUrl: voice?.previewUrl || existingVoice?.previewUrl || '',
    createdAt: voice?.createdAt || existingVoice?.createdAt || Date.now(),
    updatedAt: voice?.updatedAt || Date.now(),
    pending: false,
    status: 'complete',
    generationStatus: 'complete',
    progress: 100,
    requestId: 'complete',
  };

  if (existingIndex < 0) return [completedVoice, ...voices];
  return voices.map((item, index) => (index === existingIndex ? completedVoice : item));
};

export const buildPendingMatchingVoice = ({
  source,
  existingVoices,
  userId,
  fallbackName,
}: {
  source: any;
  existingVoices: VoiceRecord[];
  userId: string;
  fallbackName?: string;
}): VoiceRecord | null => {
  const sourceVoice = source?.voice || {};
  const sourceCharacter = source?.character || {};
  const voiceId = sourceVoice.voiceId
    || source?.matchingVoiceId
    || source?.voiceId
    || sourceCharacter.voiceId;
  if (!voiceId) return null;

  const existingVoice = existingVoices.find((voice) => voice.voiceId === voiceId);
  const now = Date.now();
  const characterAvatar = sourceCharacter.images?.[0]
    || sourceCharacter.referenceImages?.[0]
    || sourceCharacter.designPreviewImage?.url
    || sourceCharacter.designPreviewImageUrl;
  const avatarUrl = sourceVoice.avatarUrl
    || sourceVoice.signedAvatarUrl
    || characterAvatar
    || existingVoice?.avatarUrl
    || null;

  return normalizeVoiceForState({
    ...(existingVoice || {}),
    ...sourceVoice,
    userId,
    voiceId,
    voiceName: sourceVoice.voiceName
      || sourceCharacter.characterName
      || fallbackName
      || existingVoice?.voiceName
      || 'Voice',
    voiceDescription: sourceVoice.voiceDescription
      || sourceCharacter.description
      || existingVoice?.voiceDescription
      || '',
    gender: sourceVoice.gender || existingVoice?.gender || 'neutral',
    creationMethod: sourceVoice.creationMethod || existingVoice?.creationMethod || 'design',
    status: sourceVoice.status || existingVoice?.status || 'pending',
    generationStatus: sourceVoice.generationStatus || existingVoice?.generationStatus || 'pending',
    pending: true,
    progress: normalizeGenerationProgress(sourceVoice.progress ?? existingVoice?.progress),
    previewUrl: sourceVoice.previewUrl || existingVoice?.previewUrl || '',
    referenceUrl: sourceVoice.referenceUrl || existingVoice?.referenceUrl,
    signedPreviewUrl: sourceVoice.signedPreviewUrl || existingVoice?.signedPreviewUrl,
    avatarUrl,
    signedAvatarUrl: sourceVoice.signedAvatarUrl
      || (typeof avatarUrl === 'string' && avatarUrl.startsWith('http') ? avatarUrl : existingVoice?.signedAvatarUrl),
    provider: sourceVoice.provider || existingVoice?.provider || 'elevenlabs',
    requestId: sourceVoice.requestId || existingVoice?.requestId || voiceId,
    createdAt: existingVoice?.createdAt || sourceVoice.createdAt || now,
    updatedAt: now,
  });
};
